import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { emailValidation } from '@adornis/users/db/validators.js';
import { validate } from '@adornis/validation/decorators.js';
import type { UpdateResult } from 'mongodb';
import { Adresse } from '../../../../../packages/address/src/entities/Adresse.js';
import type { Bundesland } from '../../db/enums/misc-enums.js';
import { enqueueMicrosoftSchoolMemberSyncs } from '../../microsoft-graph/server/microsoft-sync-queue.js';
import { assertNotInBilling, Group } from './Group.js';
import { recursiveGroupResolver } from './recursiveGroupResolver.js';

export const ZentraleConstant = 'Zentrale';
export const SammelbeckenConstant = 'Sammelbecken';

export enum SchoolType {
  GRUNDSCHULE = 'Grundschule',
  HAUPTSCHULE = 'Hauptschule',
  REALSCHULE = 'Realschule',
  GYMNASIUM = 'Gymnasium',
}

export enum ActivatedRegisterType {
  ALL = 'all',
  ELEMENTARY_HIGH = 'elementary_high',
  SCHOOL_SCHOOL = 'school_school',
}

@Entity()
export class SchoolSubGroup extends Group {
  static override _class = 'SchoolSubGroup';

  @Field(() => School, {
    resolve: function (this: SchoolSubGroup) {
      return async (_gqlFields: BaseQLSelectionSet<School>) => {
        if (this.school) return this.school;
        return recursiveGroupResolver<School>(this, group => group._class === School._class);
      };
    },
  })
  school: Maybe<School>;
}

@Entity()
export class School extends SchoolSubGroup {
  static override _class = 'School';

  @Field(() => ID)
  imageID: Maybe<string>;

  @Field(() => ImageFile, {
    resolve: function (this: School) {
      return async () => {
        // This is a resolver, because the image is public and the publish filter does not allow anything trough on logged out status.
        // A Dataloaded field would use the publishfilter
        if (!this.imageID) return null;
        const imageFileCol = await getRawCollection<EntityData<ImageFile>>(ImageFile._collectionName);
        const imageFile = await imageFileCol.findOne({ _id: this.imageID });
        return constructValue(imageFile);
      };
    },
  })
  image: Maybe<ImageFile>;

  // sollte deputat heißen
  @Field(() => Int)
  diputatsstunden!: number;

  @Field(() => Boolean)
  isSgSchool: Maybe<boolean>;

  @Field(() => String)
  bundesland: Maybe<Bundesland>;

  @Field(() => String)
  kostenstellePrefix: Maybe<string>;

  @Field(() => String)
  accountingNumber: Maybe<string>;

  @Field(() => String)
  microsoftDomain: Maybe<string>;

  /**
   * Whether the Entra login of the people whose primary mail sits on this school moves to
   * `microsoftDomain` too, instead of staying on the tenant domain.
   *
   * Opt-in, because switching it renames the login of every one of those people on their next sync.
   * No default on purpose: a default would be written back by every save whose selection set leaves
   * this field out, and turn the toggle off behind the admin's back.
   * @see getDesiredUserPrincipalName
   */
  @Field(() => Boolean)
  microsoftDomainForLogin: Maybe<boolean>;

  @Field(() => String)
  telefonNr: Maybe<string>;

  @Field(() => String)
  schulnummer: Maybe<string>;

  @Field(() => String)
  homepageURL: Maybe<string>;

  @Field(() => String)
  linkGanztagsangebotInfo: Maybe<string>;

  @Field(() => String)
  schoolType: Maybe<SchoolType>;

  @Field(() => String)
  abwesenheitsemail: Maybe<string>;

  @validate(emailValidation)
  @Field(() => String)
  infoMail: Maybe<string>;

  @validate(emailValidation)
  @Field(() => String)
  susBewerbungReplyTo: Maybe<string>;

  @Field(() => String)
  addBGMail: Maybe<string>;

  // password for the bg mail. Is not a hashed field as it is not user relevant
  @Field(() => String)
  bgMailPassword: Maybe<string>;

  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  adresse: Maybe<Adresse>;

  // determines if the school will be shown in the public school selection on the registration form
  @Field(() => Boolean, { default: v => v ?? false })
  publicSchool: Maybe<boolean>;

  @Field(() => String, { default: v => v ?? ActivatedRegisterType.ALL })
  mode: Maybe<string>;

  /** The Kostenstelle prefix and the accounting number go into the export of the school's groups. */
  static override async beforeUpdate(oldEntity: PartialEntityData<School>, update: School): Promise<void> {
    assertNotInBilling(
      oldEntity,
      (update.has('kostenstellePrefix') &&
        (oldEntity.kostenstellePrefix ?? null) !== (update.kostenstellePrefix ?? null)) ||
        (update.has('accountingNumber') && (oldEntity.accountingNumber ?? null) !== (update.accountingNumber ?? null)),
    );

    return super.beforeUpdate(oldEntity, update);
  }

  static override async afterUpdate(
    oldEntity: EntityData<School>,
    newEntity: School,
    result: UpdateResult,
  ): Promise<void> {
    await super.afterUpdate(oldEntity, newEntity, result);

    // the domain and the login toggle are inputs of the addresses and logins of everybody linked to
    // this school. The nightly role calculation does not reach all of them - guardians and donors are
    // recalculated without a Microsoft sync - so they are queued here, when either one changes.
    if (
      oldEntity.microsoftDomain === newEntity.microsoftDomain &&
      !!oldEntity.microsoftDomainForLogin === !!newEntity.microsoftDomainForLogin
    )
      return;

    // not awaited: with its guardians a school is thousands of jobs, and the save must not wait for them
    void enqueueMicrosoftSchoolMemberSyncs(newEntity._id).catch(error =>
      logger.error({ error, schoolID: newEntity._id }, '[Microsoft Sync] Could not queue the members of the school'),
    );
  }
}
